import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
} from "@mui/material";
import {
  fetchOrderApprove,
  fetchOrderDecline,
  fetchOrderDetailList,
} from "../actions/product";

// Dodaj mapu na vrhu fajla
const shippingTypeLabels = {
  AKS: "Aks",
  D_EXPRESS: "D Express",
  BEX: "Bex",
};

const statusMessages = {
  PENDING: {
    severity: "warning",
    message: "Status: Na čekanju (porudžbina još nije obrađena).",
  },
  APPROVE: {
    severity: "success",
    message: "Status: Prihvaćeno (spremna za dalju obradu/slanje).",
  },
  DECLINED: {
    severity: "error",
    message: "Status: Odbijeno (nije prihvaćena).",
  },
};

export const formattedPrice = (price) => {
  // Konvertuj string u broj
  const numericPrice = typeof price === "string" ? parseFloat(price) : price;

  // Proveri da li je rezultat validan broj
  if (isNaN(numericPrice)) return ""; // Ako nije broj, vrati prazan string

  // Formatiraj broj sa dve decimale
  const parts = numericPrice.toFixed(2).split(".");
  // Dodaj tačku kao razdvajač hiljada
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  // Vraćanje formatiranog broja
  return parts.join(",");
};

const OrderDetail = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const { dataOrderDetailList } = useSelector((state) => state.productReducer);

  const [status, setStatus] = useState("");

  useEffect(() => {
    dispatch(fetchOrderDetailList(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (dataOrderDetailList) {
      setStatus(dataOrderDetailList.status);
    }
  }, [dataOrderDetailList]);

  const handleApproveOrder = () => {
    dispatch(fetchOrderApprove(id, navigate));
  };

  const handleDeclineOrder = () => {
    dispatch(fetchOrderDecline(id, navigate));
  };

  if (!dataOrderDetailList)
    return <div className="order-loading">Učitavanje...</div>;

  return (
    <div className="order-detail-page">
      <div className="info-alert-status-order">
        {status && (
          <Alert
            severity={statusMessages[status]?.severity}
            className="alert-info-detail-status-order"
          >
            <div className="info-order-status-text">
              {statusMessages[status]?.message}
            </div>
          </Alert>
        )}
      </div>
      <h2 className="order-title">
        <div className="order-title-text">
          Detalji porudžbine <span>#{dataOrderDetailList.receiptNumber}</span>
        </div>
        <div className="info-order-status-buttons">
          {dataOrderDetailList?.status === "PENDING" && (
            <Fragment>
              <Button
                variant="contained"
                className="mr-2"
                color="success"
                size="small"
                startIcon={<CheckIcon />}
                onClick={handleApproveOrder}
              >
                Prihvati
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<CloseIcon />}
                onClick={handleDeclineOrder}
              >
                Odbaci
              </Button>
            </Fragment>
          )}
        </div>
      </h2>

      {/* Podaci kupca */}
      <Paper className="order-section">
        <h3 className="section-title">Podaci kupca</h3>
        <div className="order-customer-info">
          <p>
            <b>Ime i prezime:</b> {dataOrderDetailList.firstName}{" "}
            {dataOrderDetailList.lastName}
          </p>
          <p>
            <b>Email:</b> {dataOrderDetailList.email}
          </p>
          <p>
            <b>Telefon:</b> {dataOrderDetailList.phone}
          </p>
          <p>
            <b>Adresa:</b> {dataOrderDetailList.address},{" "}
            {dataOrderDetailList.city} {dataOrderDetailList.postalCode}
          </p>
          {dataOrderDetailList.firm && (
            <>
              <p>
                <b>Kompanija:</b> {dataOrderDetailList.company}
              </p>
              <p>
                <b>PIB:</b> {dataOrderDetailList.pib}
              </p>
              <p>
                <b>Matični broj:</b> {dataOrderDetailList.identificationNumber}
              </p>
            </>
          )}
          <p>
            <b>Napomena:</b> {dataOrderDetailList.note || "-"}
          </p>
        </div>
      </Paper>

      {/* Stavke porudžbine */}
      <Paper className="order-section">
        <h3 className="section-title">Stavke porudžbine</h3>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Slika</TableCell>
                <TableCell>Šifra</TableCell>
                <TableCell>Naziv</TableCell>
                <TableCell>Količina</TableCell>
                <TableCell>Cena</TableCell>
                <TableCell>Ukupno</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataOrderDetailList.items?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <img
                      src={item?.images?.[0]}
                      className="table-img-custom img-fluid"
                    />
                  </TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{formattedPrice(item.price)}</TableCell>
                  <TableCell>
                    {formattedPrice(item.price * item.quantity)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Ukupno */}
      <Paper className="order-section">
        <h3 className="section-title">Rezime</h3>
        <div className="order-summary">
          <p>
            <b>Vrednost porudžbine:</b>{" "}
            {formattedPrice(dataOrderDetailList.amount)} RSD
          </p>
          <p>
            <b>Dostava:</b>{" "}
            {shippingTypeLabels[dataOrderDetailList.shippingType] ||
              dataOrderDetailList.shippingType}
          </p>
          <p className="order-total">
            <b>Ukupno:</b>{" "}
            {formattedPrice(
              dataOrderDetailList.amount + dataOrderDetailList.shipping
            )}{" "}
            RSD
          </p>
        </div>
      </Paper>
    </div>
  );
};

export default OrderDetail;
